import type {
  SearchRequest,
  SearchJob,
  SearchResultsPage,
  FeedbackRequest,
  CompanyResult,
  GewerbegebietResponse,
} from "@/types/api";
import { supabase } from "@/integrations/supabase/client";
import { ApiError } from "@/lib/api-error";
import { API_BASE_URL } from "@/lib/api-base";

const BASE_URL = API_BASE_URL;

/**
 * Resolve the current Supabase session, retrying via refreshSession() if
 * getSession() returns null (handles the post-signup SDK race window).
 * Returns only the Authorization header — callers merge with their own headers.
 */
async function getAuthHeaders(): Promise<Partial<{ Authorization: string }>> {
  const { data: getData, error: getError } = await supabase.auth.getSession();

  if (getError) {
    console.warn(
      "[auth] getSession error, attempting refresh:",
      getError.message,
    );
  }

  let session = getData?.session;

  if (!session) {
    const { data: refreshData, error: refreshError } =
      await supabase.auth.refreshSession();
    if (refreshError) {
      return {};
    }
    session = refreshData?.session ?? null;
  }

  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` };
  }
  return {};
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;

  const authHeaders = await getAuthHeaders();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...authHeaders,
  };

  let res: Response;
  try {
    res = await fetch(url, {
      headers,
      ...options,
    });
  } catch (err) {
    throw new ApiError(
      "NETWORK_ERROR",
      `Network error: Could not reach ${url}. Original error: ${err instanceof Error ? err.message : err}`,
      0,
    );
  }
  if (!res.ok) {
    let code = "UNKNOWN";
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body.code) code = body.code;
      if (body.detail) detail = body.detail;
    } catch {
      // Response was not JSON — use statusText as detail
    }
    throw new ApiError(code, detail, res.status);
  }
  return res.json();
}

// ── Gewerbegebiete ──

export async function fetchGewerbegebiete(
  lat: number,
  lng: number,
  radiusKm: number,
): Promise<GewerbegebietResponse> {
  const radiusM = Math.round(radiusKm * 1000);
  return apiFetch<GewerbegebietResponse>(
    `/api/gewerbegebiete?lat=${lat}&lng=${lng}&radius=${radiusM}`,
  );
}

// ── Credits ──

export interface CreditsStatus {
  balance: number;
  actions: Record<string, { credit_cost: number }>;
}

/** Fetch credit balance and action costs for the current company */
export async function getCreditsStatus(): Promise<CreditsStatus> {
  return apiFetch<CreditsStatus>("/credits/status");
}

// ── Credit transactions ──

export interface CreditTransaction {
  id: string;
  amount: number;
  balance_after: number;
  tx_type: string;
  action_code: string | null;
  action_name_de: string | null;
  description: string | null;
  created_at: string;
}

export interface CreditTransactionsPage {
  items: CreditTransaction[];
  total: number;
  page: number;
  per_page: number;
}

/** Fetch paginated credit transaction history */
export async function getCreditTransactions(
  page = 1,
  perPage = 20,
): Promise<CreditTransactionsPage> {
  return apiFetch<CreditTransactionsPage>(
    `/credits/transactions?page=${page}&per_page=${perPage}`,
  );
}

// ── Credit packages ──

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  currency: string;
  sort_order: number;
}

/** Fetch active credit packages (public endpoint) */
export async function getCreditPackages(): Promise<CreditPackage[]> {
  return apiFetch<CreditPackage[]>("/credits/packages");
}

/** Export search results as server-generated CSV (gated by credits) */
export async function exportCSV(jobId: string): Promise<void> {
  const url = `${BASE_URL}/search/${jobId}/export/csv`;
  const headers: Record<string, string> = {
    ...(await getAuthHeaders()),
  };

  let res: Response;
  try {
    res = await fetch(url, { headers });
  } catch (err) {
    throw new ApiError(
      "NETWORK_ERROR",
      `Network error: Could not reach ${url}. Original error: ${err instanceof Error ? err.message : err}`,
      0,
    );
  }
  if (!res.ok) {
    let code = "UNKNOWN";
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body.code) code = body.code;
      if (body.detail) detail = body.detail;
    } catch {
      /* not JSON */
    }
    throw new ApiError(code, detail, res.status);
  }

  // Trigger browser download from the response blob
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] ?? `lead-scout-${jobId.slice(0, 8)}.csv`;
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

// ── Admin action log ──

export interface AdminActionLogItem {
  timestamp: string;
  company_name: string;
  user_name: string;
  action_code: string;
  action_name_de: string;
  credit_cost: number;
  credit_cost_eur: number;
  owner_cost_cents: number;
  owner_cost_eur: number;
  reference_id: string | null;
}

export interface AdminActionLogPage {
  items: AdminActionLogItem[];
  total: number;
  page: number;
  per_page: number;
}

export interface AdminActionLogParams {
  company_id?: string;
  action_code?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

/** Fetch paginated admin action log (owner only) */
export async function getAdminActions(
  params: AdminActionLogParams = {},
): Promise<AdminActionLogPage> {
  const query = new URLSearchParams();
  if (params.company_id) query.set("company_id", params.company_id);
  if (params.action_code) query.set("action_code", params.action_code);
  if (params.date_from) query.set("date_from", params.date_from);
  if (params.date_to) query.set("date_to", params.date_to);
  if (params.page) query.set("page", String(params.page));
  if (params.per_page) query.set("per_page", String(params.per_page));
  const qs = query.toString();
  return apiFetch<AdminActionLogPage>(`/admin/actions${qs ? `?${qs}` : ""}`);
}

// ── Operator KPI metrics (CST120) ──

export interface AdminMetrics {
  counts: {
    companies: number;
    users: number;
    searches: number;
  };
  financials: {
    revenue_eur: number;
    owner_cost_eur: number;
    margin_eur: number;
  };
}

export interface AdminMetricsParams {
  date_from?: string;
  date_to?: string;
}

/** Fetch aggregate KPI metrics for the platform-owner dashboard (owner only) */
export async function getAdminMetrics(
  params: AdminMetricsParams = {},
): Promise<AdminMetrics> {
  const query = new URLSearchParams();
  if (params.date_from) query.set("date_from", params.date_from);
  if (params.date_to) query.set("date_to", params.date_to);
  const qs = query.toString();
  return apiFetch<AdminMetrics>(`/admin/metrics${qs ? `?${qs}` : ""}`);
}

// ── Translation overrides (CST207 Step 12) ──

export interface TranslationOverride {
  language: string;
  namespace: string;
  key: string;
  value: string;
}

export interface UpsertOverrideResponse {
  status: string;
  action: "insert" | "update";
  id: string;
}

export interface OverrideHistoryEntry {
  id: string;
  override_id: string;
  language: string;
  namespace: string;
  key: string;
  old_value: string | null;
  new_value: string | null;
  action: "insert" | "update" | "delete";
  changed_at: string;
  changed_by: string;
}

/** Upsert a single translation override (platform-owner only) */
export async function upsertOverride(
  override: TranslationOverride,
): Promise<UpsertOverrideResponse> {
  return apiFetch<UpsertOverrideResponse>("/api/i18n/overrides", {
    method: "POST",
    body: JSON.stringify(override),
  });
}

/** Delete a translation override (platform-owner only) */
export async function deleteOverride(
  language: string,
  namespace: string,
  key: string,
): Promise<void> {
  await apiFetch(
    `/api/i18n/overrides/${encodeURIComponent(language)}/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
    { method: "DELETE" },
  );
}

/**
 * Fetch edit history for a single translation override (platform-owner only).
 *
 * CST202: previously this data was fetched directly via Supabase RLS from the
 * frontend, which leaked to every company admin. Now routed through the backend
 * where require_owner enforces the OWNER_USER_IDS gate.
 */
export async function fetchOverrideHistory(
  namespace: string,
  key: string,
  limit = 50,
): Promise<OverrideHistoryEntry[]> {
  const params = new URLSearchParams({
    namespace,
    key,
    limit: String(limit),
  });
  return apiFetch<OverrideHistoryEntry[]>(
    `/api/i18n/overrides/history?${params.toString()}`,
  );
}

// ── Search ──

/** Submit a new search job */
export async function createSearchJob(req: SearchRequest): Promise<SearchJob> {
  const cleaned = Object.fromEntries(
    Object.entries(req).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0),
    ),
  );
  return apiFetch<SearchJob>("/search", {
    method: "POST",
    body: JSON.stringify(cleaned),
  });
}

/** Poll job status */
export async function getJobStatus(jobId: string): Promise<SearchJob> {
  return apiFetch<SearchJob>(`/search/${jobId}/status`);
}

/** Expand a completed search with additional results */
export interface ExpandResponse {
  status: string;
  job_id: string;
  additional_results: number;
}

export async function expandSearch(
  jobId: string,
  additionalResults: number,
): Promise<ExpandResponse> {
  return apiFetch<ExpandResponse>(`/search/${jobId}/expand`, {
    method: "POST",
    body: JSON.stringify({ additional_results: additionalResults }),
  });
}

/** Fetch paginated results */
export async function getJobResults(
  jobId: string,
  page = 1,
  pageSize = 25,
): Promise<SearchResultsPage> {
  return apiFetch<SearchResultsPage>(
    `/search/${jobId}/results?page=${page}&page_size=${pageSize}`,
  );
}

/** List recent jobs */
export async function listJobs(limit = 20): Promise<SearchJob[]> {
  return apiFetch<SearchJob[]>(`/search?limit=${limit}`);
}

/** Delete a job */
export async function deleteJob(jobId: string): Promise<void> {
  await apiFetch(`/search/${jobId}`, { method: "DELETE" });
}

/** Submit feedback on a result */
export interface FeedbackResponse {
  status: string;
  domain: string | null;
  signal: string;
  intent_cluster: string;
  confidence_score?: number;
  confidence_breakdown?: CompanyResult["confidence_breakdown"];
}

export async function submitFeedback(
  jobId: string,
  resultId: string,
  feedback: FeedbackRequest,
): Promise<FeedbackResponse> {
  return apiFetch<FeedbackResponse>(
    `/search/${jobId}/results/${resultId}/feedback`,
    {
      method: "POST",
      body: JSON.stringify(feedback),
    },
  );
}

/** Fetch feedback events for a job (returns map of result_id → {signal, reason}) */
export async function getJobFeedback(
  jobId: string,
): Promise<Record<string, { signal: string; reason?: string | null }>> {
  try {
    return await apiFetch<
      Record<string, { signal: string; reason?: string | null }>
    >(`/search/${jobId}/feedback`);
  } catch (err) {
    console.warn("Failed to load job feedback (non-critical):", err);
    return {};
  }
}

// ── Invite redemption ──

export interface InviteRedeemResult {
  redeemed: boolean;
  company_id: string;
  company_name: string | null;
}

/** Redeem an invite token after email verification */
export async function redeemInvite(token: string): Promise<InviteRedeemResult> {
  return apiFetch<InviteRedeemResult>(`/invites/${token}/redeem`, {
    method: "POST",
  });
}

// ── Company management (admin) ──

export interface CompanyDetails {
  id: string;
  name: string;
  // Structured address fields (replaces the old flat `address` string)
  company_street: string | null;
  company_postal_code: string | null;
  company_city: string | null;
  company_country: string | null;
  company_address_extra: string | null;
  billing_same_as_company: boolean;
  billing_street: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  billing_country: string | null;
  billing_address_extra: string | null;
  billing_contact_name: string | null;
  billing_email: string | null;
  vat_id: string | null;
  /** Original flat address string — kept for reference during migration */
  address_legacy: string | null;
  needs_address_review: boolean;
  website: string | null;
  domain: string | null;
  created_at: string | null;
}

export interface CompanyMember {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
}

export interface InviteLink {
  token: string;
  invite_url: string;
  expires_at: string;
}

/** Get company details (admin only) */
export async function getCompany(companyId: string): Promise<CompanyDetails> {
  return apiFetch<CompanyDetails>(`/companies/${companyId}`);
}

/** Structured fields accepted by the updateCompany endpoint */
export type UpdateCompanyPayload = {
  name?: string;
  website?: string;
  // Structured address fields
  company_street?: string | null;
  company_postal_code?: string | null;
  company_city?: string | null;
  company_country?: string | null;
  company_address_extra?: string | null;
  billing_same_as_company?: boolean;
  billing_street?: string | null;
  billing_postal_code?: string | null;
  billing_city?: string | null;
  billing_country?: string | null;
  billing_address_extra?: string | null;
  billing_contact_name?: string | null;
  billing_email?: string | null;
  vat_id?: string | null;
};

/** Update company details (admin only) */
export async function updateCompany(
  companyId: string,
  data: UpdateCompanyPayload,
): Promise<CompanyDetails> {
  return apiFetch<CompanyDetails>(`/companies/${companyId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

/** List company members (admin only) */
export async function listMembers(companyId: string): Promise<CompanyMember[]> {
  return apiFetch<CompanyMember[]>(`/companies/${companyId}/members`);
}

/** Generate invite link (admin only) */
export async function createInvite(companyId: string): Promise<InviteLink> {
  return apiFetch<InviteLink>(`/companies/${companyId}/invites`, {
    method: "POST",
  });
}

/** Remove a member (admin only) */
export async function removeMember(
  companyId: string,
  memberId: string,
): Promise<void> {
  await apiFetch(`/companies/${companyId}/members/${memberId}`, {
    method: "DELETE",
  });
}

/** Re-send confirmation email for a member (admin only) */
export async function resendConfirmation(
  companyId: string,
  memberId: string,
): Promise<void> {
  await apiFetch(
    `/companies/${companyId}/members/${memberId}/resend-confirmation`,
    {
      method: "POST",
    },
  );
}

/** Trigger password reset for a member (admin only) */
export async function resetMemberPassword(
  companyId: string,
  memberId: string,
): Promise<void> {
  await apiFetch(`/companies/${companyId}/members/${memberId}/reset-password`, {
    method: "POST",
  });
}

/** Fetch a single result by ID (lightweight polling). */
export async function getResultById(
  jobId: string,
  resultId: string,
): Promise<CompanyResult> {
  return apiFetch<CompanyResult>(`/search/${jobId}/results/${resultId}`);
}

/** Trigger deep enrichment for a single result. Returns status code. */
export async function enrichResult(
  jobId: string,
  resultId: string,
): Promise<number> {
  const url = `${BASE_URL}/search/${jobId}/results/${resultId}/enrich`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await getAuthHeaders()),
  };

  let res: Response;
  try {
    res = await fetch(url, { method: "POST", headers });
  } catch (err) {
    throw new ApiError(
      "NETWORK_ERROR",
      `Network error: Could not reach ${url}. Original error: ${err instanceof Error ? err.message : err}`,
      0,
    );
  }

  // 202 = accepted, 409 = already in progress (both are "success" for the caller)
  if (res.status === 202 || res.status === 409) {
    return res.status;
  }

  let code = "UNKNOWN";
  let detail = res.statusText;
  try {
    const body = await res.json();
    if (body.code) code = body.code;
    if (body.detail) detail = body.detail;
  } catch {
    /* not JSON */
  }
  throw new ApiError(code, detail, res.status);
}

/** Fetch all results across pages */
export async function fetchAllResults(jobId: string): Promise<CompanyResult[]> {
  const allResults: CompanyResult[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const data = await getJobResults(jobId, page);
    allResults.push(...data.results);
    totalPages = data.total_pages;
    page++;
  }

  return allResults;
}
